import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  PDFEditorState, 
  PDFDocument, 
  PDFPage, 
  CanvasElement, 
  PropertyReportModel,
  PropertyEntryModel,
  ImageWithMetadata
} from '../../types/models';

// Helper function to create a new page
const createNewPage = (id: string): PDFPage => ({
  id,
  elements: [],
  width: 794, // A4 width in pixels at 96 DPI
  height: 1123, // A4 height in pixels at 96 DPI
  backgroundColor: '#ffffff',
});

// Helper function to create a new document
const createNewDocument = (title: string, reportId?: string): PDFDocument => ({
  id: `doc_${Date.now()}`,
  title,
  pages: [createNewPage(`page_${Date.now()}`)],
  reportId,
  createdAt: new Date(),
  updatedAt: new Date(),
});

const initialState: PDFEditorState = {
  currentDocument: null,
  currentPageIndex: 0,
  selectedElementIds: [],
  clipboardElements: [],
  history: [],
  historyIndex: -1,
  zoom: 1,
  gridEnabled: true,
  snapToGrid: true,
  isLoading: false,
  isExporting: false,
  exportStatus: null,
  error: null,
  templates: [],
  reportData: null,
  availableAssets: {
    images: [],
    entries: [],
  },
};

const pdfEditorSlice = createSlice({
  name: 'pdfEditor',
  initialState,
  reducers: {
    // Document Management
    createDocument: (state, action: PayloadAction<{ title: string; reportId?: string }>) => {
      const newDoc = createNewDocument(action.payload.title, action.payload.reportId);
      state.currentDocument = newDoc;
      state.currentPageIndex = 0;
      state.selectedElementIds = [];
      state.history = [newDoc];
      state.historyIndex = 0;
    },

    loadDocument: (state, action: PayloadAction<PDFDocument>) => {
      state.currentDocument = action.payload;
      state.currentPageIndex = 0;
      state.selectedElementIds = [];
      state.history = [action.payload];
      state.historyIndex = 0;
    },

    updateDocumentTitle: (state, action: PayloadAction<string>) => {
      if (state.currentDocument) {
        state.currentDocument.title = action.payload;
        state.currentDocument.updatedAt = new Date();
      }
    },

    // Page Management
    addPage: (state) => {
      if (state.currentDocument) {
        const newPage = createNewPage(`page_${Date.now()}`);
        state.currentDocument.pages.push(newPage);
        state.currentPageIndex = state.currentDocument.pages.length - 1;
      }
    },

    deletePage: (state, action: PayloadAction<number>) => {
      if (state.currentDocument && state.currentDocument.pages.length > 1) {
        state.currentDocument.pages.splice(action.payload, 1);
        if (state.currentPageIndex >= state.currentDocument.pages.length) {
          state.currentPageIndex = state.currentDocument.pages.length - 1;
        }
      }
    },

    duplicatePage: (state, action: PayloadAction<number>) => {
      if (state.currentDocument) {
        const pageToClone = state.currentDocument.pages[action.payload];
        const newPage: PDFPage = {
          ...pageToClone,
          id: `page_${Date.now()}`,
          elements: pageToClone.elements.map(el => ({
            ...el,
            id: `${el.id}_copy_${Date.now()}`,
          })),
        };
        state.currentDocument.pages.splice(action.payload + 1, 0, newPage);
        state.currentPageIndex = action.payload + 1;
      }
    },

    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPageIndex = action.payload;
      state.selectedElementIds = [];
    },

    // Element Management
    addElement: (state, action: PayloadAction<CanvasElement>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        const element = {
          ...action.payload,
          zIndex: state.currentDocument.pages[state.currentPageIndex].elements.length,
        };
        state.currentDocument.pages[state.currentPageIndex].elements.push(element);
        state.selectedElementIds = [element.id];
      }
    },

    updateElement: (state, action: PayloadAction<{ id: string; updates: Partial<CanvasElement> }>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        const elementIndex = state.currentDocument.pages[state.currentPageIndex].elements
          .findIndex(el => el.id === action.payload.id);
        
        if (elementIndex !== -1) {
          Object.assign(
            state.currentDocument.pages[state.currentPageIndex].elements[elementIndex],
            action.payload.updates
          );
          state.currentDocument.updatedAt = new Date();
        }
      }
    },

    deleteElements: (state, action: PayloadAction<string[]>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        state.currentDocument.pages[state.currentPageIndex].elements = 
          state.currentDocument.pages[state.currentPageIndex].elements
            .filter(el => !action.payload.includes(el.id));
        state.selectedElementIds = [];
      }
    },

    duplicateElements: (state, action: PayloadAction<string[]>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        const elementsToDuplicate = state.currentDocument.pages[state.currentPageIndex].elements
          .filter(el => action.payload.includes(el.id));
        
        const duplicatedElements = elementsToDuplicate.map(el => ({
          ...el,
          id: `${el.id}_copy_${Date.now()}`,
          x: el.x + 20,
          y: el.y + 20,
          zIndex: state.currentDocument!.pages[state.currentPageIndex].elements.length + 1,
        }));

        state.currentDocument.pages[state.currentPageIndex].elements.push(...duplicatedElements);
        state.selectedElementIds = duplicatedElements.map(el => el.id);
      }
    },

    // Selection Management
    selectElements: (state, action: PayloadAction<string[]>) => {
      state.selectedElementIds = action.payload;
    },

    clearSelection: (state) => {
      state.selectedElementIds = [];
    },

    // Layer Management
    bringToFront: (state, action: PayloadAction<string[]>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        const elements = state.currentDocument.pages[state.currentPageIndex].elements;
        const maxZIndex = Math.max(...elements.map(el => el.zIndex));
        
        action.payload.forEach((id, index) => {
          const element = elements.find(el => el.id === id);
          if (element) {
            element.zIndex = maxZIndex + index + 1;
          }
        });
      }
    },

    sendToBack: (state, action: PayloadAction<string[]>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        const elements = state.currentDocument.pages[state.currentPageIndex].elements;
        const minZIndex = Math.min(...elements.map(el => el.zIndex));
        
        action.payload.forEach((id, index) => {
          const element = elements.find(el => el.id === id);
          if (element) {
            element.zIndex = minZIndex - action.payload.length + index;
          }
        });
      }
    },

    // Clipboard Operations
    copyElements: (state, action: PayloadAction<string[]>) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex]) {
        const elementsToCopy = state.currentDocument.pages[state.currentPageIndex].elements
          .filter(el => action.payload.includes(el.id));
        state.clipboardElements = elementsToCopy;
      }
    },

    pasteElements: (state) => {
      if (state.currentDocument && state.currentDocument.pages[state.currentPageIndex] && state.clipboardElements.length > 0) {
        const pastedElements = state.clipboardElements.map(el => ({
          ...el,
          id: `${el.id}_paste_${Date.now()}`,
          x: el.x + 20,
          y: el.y + 20,
          zIndex: state.currentDocument!.pages[state.currentPageIndex].elements.length + 1,
        }));

        state.currentDocument.pages[state.currentPageIndex].elements.push(...pastedElements);
        state.selectedElementIds = pastedElements.map(el => el.id);
      }
    },

    // View Controls
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = Math.max(0.1, Math.min(5, action.payload));
    },

    toggleGrid: (state) => {
      state.gridEnabled = !state.gridEnabled;
    },

    toggleSnapToGrid: (state) => {
      state.snapToGrid = !state.snapToGrid;
    },

    // History Management
    addToHistory: (state) => {
      if (state.currentDocument) {
        // Remove any history after current index
        state.history = state.history.slice(0, state.historyIndex + 1);
        
        // Add current state to history
        state.history.push(JSON.parse(JSON.stringify(state.currentDocument)));
        state.historyIndex = state.history.length - 1;

        // Limit history size
        if (state.history.length > 50) {
          state.history.shift();
          state.historyIndex--;
        }
      }
    },

    undo: (state) => {
      if (state.historyIndex > 0) {
        state.historyIndex--;
        state.currentDocument = JSON.parse(JSON.stringify(state.history[state.historyIndex]));
        state.selectedElementIds = [];
      }
    },

    redo: (state) => {
      if (state.historyIndex < state.history.length - 1) {
        state.historyIndex++;
        state.currentDocument = JSON.parse(JSON.stringify(state.history[state.historyIndex]));
        state.selectedElementIds = [];
      }
    },

    // Data Integration
    setReportData: (state, action: PayloadAction<PropertyReportModel>) => {
      state.reportData = action.payload;
    },

    setAvailableAssets: (state, action: PayloadAction<{
      images: ImageWithMetadata[];
      entries: PropertyEntryModel[];
    }>) => {
      state.availableAssets = action.payload;
    },

    // Loading and Error States
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setExporting: (state, action: PayloadAction<boolean>) => {
      state.isExporting = action.payload;
      if (!action.payload) {
        state.exportStatus = null; // Clear status when export finishes
      }
    },

    setExportStatus: (state, action: PayloadAction<string | null>) => {
      state.exportStatus = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  createDocument,
  loadDocument,
  updateDocumentTitle,
  addPage,
  deletePage,
  duplicatePage,
  setCurrentPage,
  addElement,
  updateElement,
  deleteElements,
  duplicateElements,
  selectElements,
  clearSelection,
  bringToFront,
  sendToBack,
  copyElements,
  pasteElements,
  setZoom,
  toggleGrid,
  toggleSnapToGrid,
  addToHistory,
  undo,
  redo,
  setReportData,
  setAvailableAssets,
  setLoading,
  setExporting,
  setExportStatus,
  setError,
  clearError,
} = pdfEditorSlice.actions;

// Selectors
export const selectCurrentDocument = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.currentDocument;
export const selectCurrentPageIndex = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.currentPageIndex;
export const selectSelectedElementIds = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.selectedElementIds;
export const selectZoom = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.zoom;
export const selectIsLoading = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.isLoading;
export const selectIsExporting = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.isExporting;
export const selectExportStatus = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.exportStatus;
export const selectError = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.error;
export const selectReportData = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.reportData;
export const selectAvailableAssets = (state: { pdfEditor: PDFEditorState }) => state.pdfEditor.availableAssets;

export default pdfEditorSlice.reducer;